import { useEffect, useRef, useState } from 'react';

import { A11y, Keyboard, Navigation, Zoom } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/zoom';

import './ImageLightbox.css';

const CLOSE_DISTANCE = 120;
const DOUBLE_TAP_DELAY = 280;

function ImageLightbox({
  activeIndex,
  images,
  isOpen,
  onClose,
}) {
  const swiperRef = useRef(null);
  const touchStartRef = useRef(null);
  const clickTimerRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(activeIndex ?? 0);
  const [dragY, setDragY] = useState(0);
  const [isClosingDrag, setIsClosingDrag] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);

      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (activeIndex !== null) {
      setCurrentIndex(activeIndex);
    }
  }, [activeIndex]);

  if (!isOpen || activeIndex === null) return null;

  const handleTouchStart = (event) => {
    const swiper = swiperRef.current;

    if (swiper?.zoom?.scale > 1) {
      touchStartRef.current = null;
      return;
    }

    const touch = event.touches[0];

    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };

    setDragY(0);
    setIsClosingDrag(false);
  };

  const handleTouchMove = (event) => {
    if (!touchStartRef.current) return;

    const swiper = swiperRef.current;

    if (swiper?.zoom?.scale > 1) {
      setDragY(0);
      setIsClosingDrag(false);
      return;
    }

    const touch = event.touches[0];

    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    const isVertical =
      Math.abs(deltaY) > Math.abs(deltaX) * 1.15;

    if (!isVertical || deltaY <= 0) {
      setDragY(0);
      setIsClosingDrag(false);
      return;
    }

    setIsClosingDrag(true);
    setDragY(deltaY);
  };

  const handleTouchEnd = () => {
    touchStartRef.current = null;

    if (isClosingDrag && dragY >= CLOSE_DISTANCE) {
      onClose();
      return;
    }

    setDragY(0);
    setIsClosingDrag(false);
  };

  const handleNavigationClick = (direction) => {
    const swiper = swiperRef.current;

    if (!swiper || isZoomed || isClosingDrag) return;

    /*
     * Небольшая задержка нужна, чтобы первый tap из double tap
     * не перелистнул фотографию до того, как Swiper распознает zoom.
     */
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    clickTimerRef.current = setTimeout(() => {
      if (direction === 'previous') {
        swiper.slidePrev();
      } else {
        swiper.slideNext();
      }

      clickTimerRef.current = null;
    }, DOUBLE_TAP_DELAY);
  };

  const handleDoubleTap = (swiper) => {
    /*
     * Отменяем одиночный tap, который ожидал перелистывания.
     */
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }

    swiper.zoom.toggle();
  };

  const handleZoomChange = (swiper, scale) => {
    setIsZoomed(scale > 1);

    if (scale > 1) {
      setDragY(0);
      setIsClosingDrag(false);
    }
  };

  const closeProgress = Math.min(dragY / 350, 1);
  const scale = 1 - closeProgress * 0.08;
  const overlayOpacity = 1 - closeProgress * 0.65;

  return (
    <div
      className="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        backgroundColor: `rgba(255, 255, 255, ${overlayOpacity})`,
      }}
    >
      <button
        className="image-lightbox__close"
        type="button"
        aria-label="Close gallery"
        onClick={onClose}
      >
        ×
      </button>

      <div
        className={`image-lightbox__viewer ${
          isClosingDrag
            ? 'image-lightbox__viewer_dragging'
            : ''
        }`}
        style={{
          transform: `translate3d(0, ${dragY}px, 0) scale(${scale})`,
        }}
      >
        <Swiper
          className="image-lightbox__swiper"
          modules={[A11y, Keyboard, Navigation, Zoom]}
          initialSlide={activeIndex}
          slidesPerView={1}
          spaceBetween={0}
          speed={300}
          threshold={6}
          resistance
          resistanceRatio={0.65}
          touchAngle={45}
          grabCursor
          keyboard={{
            enabled: true,
          }}
          navigation
          zoom={{
            maxRatio: 4,
            minRatio: 1,

            /*
             * Выключаем встроенный toggle,
             * потому что double tap обрабатываем сами.
             */
            toggle: false,
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            setCurrentIndex(swiper.activeIndex);
          }}
          onSlideChange={(swiper) => {
            setCurrentIndex(swiper.activeIndex);

            if (swiper.zoom.scale > 1) {
              swiper.zoom.out();
            }

            setIsZoomed(false);
          }}
          onDoubleTap={handleDoubleTap}
          onZoomChange={handleZoomChange}
        >
          {images.map((image, index) => (
            <SwiperSlide
              className="image-lightbox__slide"
              key={image.id ?? image.src}
            >
              <div className="swiper-zoom-container">
                <img
                  className="image-lightbox__image"
                  src={image.src}
                  alt={image.alt}
                  draggable="false"
                />

                {!isZoomed && index === currentIndex && (
                  <>
                    <button
                      className="
                        image-lightbox__tap-zone
                        image-lightbox__tap-zone_direction_left
                      "
                      type="button"
                      aria-label="Previous image"
                      onClick={() =>
                        handleNavigationClick('previous')
                      }
                    />

                    <button
                      className="
                        image-lightbox__tap-zone
                        image-lightbox__tap-zone_direction_right
                      "
                      type="button"
                      aria-label="Next image"
                      onClick={() =>
                        handleNavigationClick('next')
                      }
                    />
                  </>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="image-lightbox__counter">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
}

export default ImageLightbox;
